import { InputAdornment, TextField } from "@mui/material";
import { Col, Row, Form } from "react-bootstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { useLoader } from "../../context/LoaderContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createMetadataMapping,
  editMetaDataMapping,
} from "../../api/metadataMappingApi/metadatMappingApi";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";

function not(a: readonly number[], b: readonly number[]) {
  return a.filter((value) => !b.includes(value));
}

function intersectionLeft(a: readonly number[], b: any) {
  return b.filter((column: any) =>
    a.includes(column?.metadataItemId || column?.metadataSelectedItemId)
  );
}

function intersectionRight(a: readonly number[], b: any) {
  return b.filter((column: any) =>
    a.includes(column?.metadataSelectedItemId || column?.metadataItemId)
  );
}

interface MetadatMappingManagementActionProps {
  businessUnitData: any;
}

const MetadatMappingManagementAction: React.FC<
  MetadatMappingManagementActionProps
> = ({ businessUnitData }) => {
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [searchTermLeft, setSearchTerm] = useState("");// NOSONAR
  const [searchTermRight, setSearchTermRight] = useState("");
  const location = useLocation();
  const action = location.state?.action || {};
  const rowData = location.state?.rowData || {};
  const { showSnackbar } = useSnackbar();
  const [history, setHistory] = useState<
    Array<{
      left: readonly number[];
      right: readonly number[];
      checked: readonly number[];
    }>
  >([]);
  const [checked, setChecked] = React.useState<readonly number[]>([]);
  const [left, setLeft] = useState<readonly number[]>([]);
  const [columnHeaders, setColumnHeaders] = useState<readonly any[]>([]);
  const [right, setRight] = useState<any>([]);
  const [formData, setFormData] = useState({
    metadataGroupName: rowData?.metadataGroupName || "",
  });
  const leftChecked = intersectionLeft(checked, left);
  const rightChecked = intersectionRight(checked, right);
  const [isLeftToright, setIsLeftToRight] = useState<any>(null);// NOSONAR
  const { setLoading } = useLoader();
  const [errors, setErrors] = useState<{
    groupNameError?: string;
    selectItemError?: string;
  }>({});

  const handleToggle = (value: number) => () => {
    saveToHistory();
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setChecked(newChecked);
  };

  const handleAllRight = () => {
    saveToHistory();
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
    setIsLeftToRight(false);
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
    setIsLeftToRight(true);
  };

  const handleAllLeft = () => {
    saveToHistory();
    setLeft(left.concat(right));
    setRight([]);
  };

  const saveToHistory = () => {
    setHistory((prev) => [...prev, { left, right, checked }]);
  };

  const undoLastAction = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setLeft(lastState.left);
      setRight(lastState.right);
      setChecked(lastState.checked);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const handleConfirmDialogClose = (confirm: boolean) => {
    try {
      setConfirmDialogOpen(false);
      if (confirm) {
        navigate("/metadatamapping");
      } else {
        setConfirmDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleConfirmDialogClose", error);
    }
  };

  const handelCancel = () => {
    setConfirmDialogOpen(true);
  };

  const customListLeft = (items: readonly number[], searchTermLeft: string) => {
    return (
      <Paper sx={{ width: "100%", height: 490, overflow: "auto" }}>
        <List
          dense
          component="div"
          role="list"
          sx={{ height: "100%", overflowY: "auto" }}
        >
          {items.length === 0 ? (
            <ListItemText sx={{ textAlign: "center", color: "gray" }}>
              No items available...
            </ListItemText>
          ) : (
            items
              .filter((item: any) =>
                item?.metadataItemName
                  ?.toLowerCase()
                  .includes(searchTermLeft?.toLowerCase())
              )
              .map((value: any) => {
                const isSelected =
                  checked.indexOf(
                    value?.metadataItemId || value.metadataSelectedItemId
                  ) !== -1;
                return (
                  <ListItemButton
                    key={value?.metadataItemId}
                    role="listitem"
                    onClick={handleToggle(
                      value?.metadataItemId || value?.metadataSelectedItemId
                    )}
                    sx={{
                      backgroundColor: isSelected
                        ? " rgb(77, 123, 167)"
                        : "transparent",
                      border: isSelected ? "1px solid darkblue" : "",
                      color: isSelected ? "white" : "default",
                      "&:hover": {
                        backgroundColor: isSelected ? "darkblue" : "lightgray",
                      },
                    }}
                  >
                    <ListItemText primary={value.metadataItemName} />
                  </ListItemButton>
                );
              })
          )}
        </List>
      </Paper>
    );
  };

  const customListRight = (
    items: readonly number[],
    searchTermRight: string
  ) => {
    return (
      <Paper sx={{ width: "100%", height: 490, overflow: "auto" }}>
        <List
          dense
          component="div"
          role="list"
          sx={{ height: "100%", overflowY: "auto" }}
        >
          {items.length === 0 ? (
            <ListItemText sx={{ textAlign: "center", color: "gray" }}>
              No items available...
            </ListItemText>
          ) : (
            items
              .filter((item: any) =>
                item?.metadataItemName
                  ?.toLowerCase()
                  .includes(searchTermRight?.toLowerCase())
              )
              .map((value: any) => {
                const isSelected =
                  checked.indexOf(
                    value?.metadataSelectedItemId || value?.metadataItemId
                  ) !== -1;
                return (
                  <ListItemButton
                    key={value.metadataSelectedItemId}
                    role="listitem"
                    onClick={handleToggle(
                      value?.metadataSelectedItemId || value?.metadataItemId
                    )}
                    sx={{
                      backgroundColor: isSelected
                        ? " rgb(77, 123, 167)"
                        : "transparent",
                      border: isSelected ? "1px solid darkblue" : "",
                      color: isSelected ? "white" : "default",
                      "&:hover": {
                        backgroundColor: isSelected ? "darkblue" : "lightgray",
                      },
                    }}
                  >
                    <ListItemText primary={value.metadataItemName} />
                  </ListItemButton>
                );
              })
          )}
        </List>
      </Paper>
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchChangeRight = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchTermRight(event.target.value);
  };

  const handleDropdownChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    const selectedProcessStage = columnHeaders.find(
      (metadata: any) => metadata.metadataGroupName === value
    );
    if (selectedProcessStage) {
      getMetadata(selectedProcessStage.metadataGroupId);
    } else {
      getMetadata();
    }
  };

  const getMetadata = async (MetadataGroupId: number = 0) => {
    setLoading(true);
    const payloadforColumnHeaders = {
      MetadataGroupId: MetadataGroupId || rowData?.metadataGroupId,
      UserId: LocalStorageUtil.getItem("userId"),
    };
    const payload = { paramPost: null };
    const paramPost =
      action !== "Edit" && !rowData?.metadataGroupId && !MetadataGroupId
        ? payload
        : payloadforColumnHeaders;
    try {
      const data = await editMetaDataMapping(paramPost);
      setColumnHeaders(data.metaDataModel);
      const rightItems = data.metaDataModel
        .filter((e: any) => e.metadataSelectedItemName != null)
        .map((e: any) => ({
          ...e,
          metadataItemName: e.metadataSelectedItemName,
        }));
      setRight(rightItems);
      const leftItems = data.metaDataModel.filter(
        (e: any) =>
          !rightItems.some(
            (r: any) => r.metadataItemName === e.metadataItemName
          )
      );
      setLeft(leftItems);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMetadata();
  }, []);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const newErrors: { groupNameError?: string; selectItemError?: string } =
        {};
      if (formData.metadataGroupName === "") {
        newErrors.groupNameError = "Group Name is required";
      }
      if (right.length === 0) {
        newErrors.selectItemError = "Please select atleast one item";
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
      } else {
        setErrors({});
        const length = right.length;
        const newValues = checked.map((val) => val);
        let MetadataItemValuesId = new Array(length).fill(0);
        const InactiveIds = checked.map((item) => item);
        MetadataItemValuesId = MetadataItemValuesId.map((id) =>
          InactiveIds.includes(id) ? 0 : id
        );
        const uniqueNewValues = Array.from(new Set(newValues));
        MetadataItemValuesId = MetadataItemValuesId.filter((id) => id !== 0);
        MetadataItemValuesId.push(...uniqueNewValues);
        const payload = {
          UserId: LocalStorageUtil.getItem("userId"),
          MetadataGroupId:
            rowData.metadataGroupId === undefined ? 0 : rowData.metadataGroupId,
          MetadataItemValuesId: isLeftToright ? [] : MetadataItemValuesId,
          InactiveId: !isLeftToright ? [] : InactiveIds,
          UndoFlag: false,
        };

        const response = await createMetadataMapping(payload);
        console.log("Create Metadata Mapping:", response);
        navigate("/metadatamapping");
        showSnackbar("Metadata Mapping Updated Successfully");
      }
    } catch (error) {
      console.error("Error creating metadata group:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      {action === "Edit" ? (
        <NavigationBar
          menuTitle="Configuration"
          title="Metadata Management"
          screenTitle="Metadata Mapping"
          text="Metadata Mapping - Update Metadata Mapping"
        />
      ) : (
        <NavigationBar
          menuTitle="Configuration"
          title="Metadata Management"
          screenTitle="Metadata Mapping"
          text="Metadata Mapping - Create Metadata Mapping"
        />
      )}
      <div style={{ marginLeft: "4vh" }} className="ag-theme-quartz-dark">
        <h4
          className="form-heading"
          style={{ color: "var(--aggrid-text-color)", fontSize: "12px" }}
        >
          Basic Information
        </h4>
        <Form>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group controlId="formCustomer">
                <Form.Label
                  className="input-labels"
                  style={{
                    color: "var(--aggrid-text-color)",
                    fontSize: "11px",
                  }}
                >
                  Group Name<span className="required">*</span>
                </Form.Label>
                <Form.Select
                  name="metadataGroupName"
                  value={formData.metadataGroupName}
                  onChange={handleDropdownChange}
                  disabled={action === "Edit"}
                  className="select-dropdown"
                  isInvalid={!!errors.groupNameError}
                  style={{ fontSize: "11px" }}
                >
                  <option value="">Select</option>
                  {columnHeaders
                    .filter((metadata: any) =>
                      metadata.metadataGroupName?.trim()
                    ) 
                    .map((metadata: any) => (
                      <option
                        key={`${metadata.metadataGroupId}-${metadata.metadataGroupName}`} 
                        value={metadata.metadataGroupName}
                      >
                        {metadata.metadataGroupName}
                      </option>
                    ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.groupNameError}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Available<span className="required">*</span>
              </Form.Label>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTermLeft}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  marginBottom: "10px",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    height: "35px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
                      fontSize: "11px",
                    },
                  },
                }}
                variant="outlined"
              />
              <div style={{ fontSize: "11px" }}>
                {customListLeft(left, searchTermLeft)}
              </div>
            </Col>
            <Col
              md={1}
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleAllRight}
                disabled={left.length === 0}
                aria-label="move all right"
                className="arrow-button"
              >
                ≫
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
                className="arrow-button"
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
                className="arrow-button"
              >
                &lt;
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={handleAllLeft}
                disabled={right.length === 0}
                aria-label="move all left"
                className="arrow-button"
              >
                ≪
              </Button>
              <Button
                sx={{ my: 0.5, height: "25px" }}
                variant="outlined"
                size="small"
                onClick={undoLastAction}
                disabled={history.length === 0}
                aria-label="undo last action"
                className="arrow-button"
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    fontSize: "14px",
                    height: "14px",
                    width: "14px",
                  }}
                />
              </Button>
            </Col>
            <Col md={4}>
              <Form.Label
                className="input-labels"
                style={{ color: "var(--aggrid-text-color)", fontSize: "11px" }}
              >
                Enabled<span className="required">*</span>
              </Form.Label>
              <TextField
                fullWidth
                placeholder="Search"
                value={searchTermRight}
                onChange={handleSearchChangeRight}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  "& .MuiOutlinedInput-root": {
                    height: "30px",
                    borderRadius: "8px",
                    "& fieldset": {
                      borderColor: "#ccc",
                    },
                    "&:hover fieldset": {
                      borderColor: "#888",
                    },
                    "& input": {
                      fontSize: "11px",
                    },
                  },
                }}
                variant="outlined"
              />
              <div style={{ fontSize: "11px" }}>
                {customListRight(right, searchTermRight)}
              </div>
              {errors.selectItemError && (
                <p style={{ color: "red" }}>{errors.selectItemError}</p>
              )}
            </Col>
          </Row>
          <Row>
            <Col
              md={12}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              <Button
                variant="contained"
                className="update-button"
                onClick={handleCreate}
              >
                {action === "Edit" ? "Update" : "Create"}
              </Button>
              <Button
                variant="contained"
                className="cancel-button"
                onClick={handelCancel}
                style={{
                  marginRight: "1.5em",
                }}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        title={`Metadata Mapping  - ${
          action === "Edit"
            ? "Update Metadata Mapping"
            : "Create New Metadata Mapping"
        }`}
        content="Are you sure you want to Cancel?"
      />
    </div>
  );
};

export default MetadatMappingManagementAction;
